import { Grid, TextField } from '@mui/material';
import React, { useState } from 'react'
import moment from "moment";
import {
  Stack,
  InputAdornment,
  Typography,
  Box,
  Slider,
  ToggleButtonGroup,
  ToggleButton
} from "@mui/material";
import { useDispatch } from 'react-redux';
import { saveAsset, saveLoanDetails, updateAsset, updateLoanDetails } from 'src/store/slices/applicationFormSlice';
import { merge } from 'lodash';
import { NumericFormatCustom } from 'src/utils/currencyMaskFormat';
import { convertValueToDecimal } from 'src/utils/commonFunctions';

const dollarStringOptions = {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
};

const LoanDetails = ({ asset, loanDetails }) => {
  const dispatch = useDispatch();
  const { balloon = 0, term = 0 } = loanDetails
  const [mode, setMode] = useState({ deposit: "percentage", balloon: "percentage" });
  const isDepositPerCheck = mode.deposit === "percentage";
  const isBalloonPerCheck = mode.balloon === "percentage";
  const principalAmount = asset?.assetValue
  const netAssetValue = principalAmount - loanDetails?.depositAmount
  const [maxBalloon, setMaxBallon] = useState(30);
  const [balloonMarks, setBalloonMarks] = useState([
    { value: 0, label: 0 },
    { value: 10, label: 10 },
    { value: 20, label: 20 },
    { value: 30, label: 30 },
  ]);

  const handle = {
    onChangeLoanDetailsField: (field, value) => {
      if (field === "deposit") {
        if (mode.deposit === "percentage") {
          if (value < 0) {
            return;
          }
          dispatch(saveLoanDetails({ ...loanDetails, [field]: value, depositAmount: handle.getAmount({ percentage: Number(value) }) }))
        } else {
          dispatch(saveLoanDetails({ ...loanDetails, [field]: handle.getPercentageValue({ amount: Number(value) }), depositAmount: Math.abs(value) }))
        }
      }
      if (field === "balloon") {

        if (mode.balloon === "percentage") {
          if (value < 0) {
            return;
          }
          dispatch(saveLoanDetails({ ...loanDetails, [field]: value, balloonAmount: handle.getBalloonAmount({ percentage: Number(value) }) }))
        } else {
          dispatch(saveLoanDetails({ ...loanDetails, [field]: handle.getBalloonPercentage({ amount: Number(value) }), balloonAmount: Math.abs(value) }))
        }
      }
      return
    },
    handleMaxBalloon: (TERM) => {
      if (TERM === 48) {
        setBalloonMarks([
          { value: 0, label: 0 },
          { value: 10, label: 10 },
          { value: 20, label: 20 },
          { value: 30, label: 30 },
          { value: 40, label: 40 },
        ])
        return setMaxBallon(40)
      }
      if (TERM === 36) {
        setBalloonMarks([
          { value: 0, label: 0 },
          { value: 10, label: 10 },
          { value: 20, label: 20 },
          { value: 30, label: 30 },
          { value: 40, label: 40 },
          { value: 50, label: 50 },
        ])
        return setMaxBallon(50)
      }
      if (TERM === 24) {
        setBalloonMarks([
          { value: 0, label: 0 },
          { value: 10, label: 10 },
          { value: 20, label: 20 },
          { value: 30, label: 30 },
          { value: 40, label: 40 },
          { value: 50, label: 50 },
          { value: 60, label: 60 },
        ])
        return setMaxBallon(60)
      }
      if (TERM === 12) {
        setBalloonMarks([
          { value: 0, label: 0 },
          { value: 10, label: 10 },
          { value: 20, label: 20 },
          { value: 30, label: 30 },
          { value: 40, label: 40 },
          { value: 50, label: 50 },
          { value: 60, label: 60 },
          { value: 70, label: 70 },
        ])
        return setMaxBallon(70)
      }

      setBalloonMarks([
        { value: 0, label: 0 },
        { value: 10, label: 10 },
        { value: 20, label: 20 },
        { value: 30, label: 30 },
      ])

      return setMaxBallon(30)
    },
    handleTerm: (value) => {
      handle.onChangeLoanDetailsField("term", value)
      handle.handleMaxBalloon(value)
      const termInYears = value / 12;
      const ageOfAssetMoment = moment(asset?.ageOfAsset, "YYYY");
      const endOfTermYear = moment().add(termInYears, "year");
      const ageOfAssetAtEnd = endOfTermYear.diff(ageOfAssetMoment, "years");
      dispatch(saveAsset({ ageOfAssetAtEnd }));
      const update = merge({
        ...asset,
        ...{ ageOfAssetAtEnd },
      });
      dispatch(updateAsset(update));
      dispatch(saveLoanDetails({ term: value }));
      dispatch(
        updateLoanDetails(
          merge({
            ...loanDetails,
            ...{
              term: value,
            },
          })
        )
      );
    },
    handleModeChange: (key, newMode) => {
      if (newMode !== null) {

        setMode({
          ...mode,
          [key]: newMode
        });
      }
    },
    loanDetailsFn: (value) => {
      if (loanDetails._id)
        dispatch(
          updateLoanDetails(
            merge({
              ...loanDetails,
              ...value,
            })
          )
        )
    },
    getBalloonAmount: ({ percentage }) => (percentage / 100) * netAssetValue,
    getBalloonPercentage: ({ balloonAmount }) => {
      const balloonPercentage = (balloonAmount / netAssetValue) * 100;
      return isFinite(balloonPercentage) ? balloonPercentage : 0;
    },
    getAmount: ({ percentage }) => (percentage / 100) * principalAmount,
    getPercentageValue: ({ amount }) => {
      const depositPercentage = (amount / principalAmount) * 100;
      return isFinite(depositPercentage) ? depositPercentage : 0;
    },

  }

  return (
    <Grid container spacing={3}>
      <Grid item sm={12} md={6} lg={4}>
        <Stack direction="column">
          <Typography variant="subtitle2" sx={{ marginBottom: "5px" }}>
            Deposit {!isNaN(loanDetails?.deposit) ? Math.round(loanDetails?.deposit * 100) / 100 : 0}% |{" "}
            {(loanDetails?.depositAmount ? loanDetails?.depositAmount : 0).toLocaleString(
              "en-US",
              dollarStringOptions,
            )}
          </Typography>
          <Box display="flex" alignItems="center">
            <TextField
              name="deposit"
              style={{ background: "#fff" }}
              value={isDepositPerCheck ? loanDetails?.deposit : loanDetails?.depositAmount}
              onChange={(event) => {
                const value = event.target.value;
                const isValueUpdated = Number(value) !== Number(loanDetails?.deposit);

                if (isValueUpdated) {
                  handle.onChangeLoanDetailsField("deposit", value || 0);
                }
              }}
              onBlur={() => {
                handle.loanDetailsFn({
                  deposit: convertValueToDecimal({ value: loanDetails?.deposit }) || 0,
                  depositAmount: convertValueToDecimal({ value: loanDetails?.depositAmount }) || 0,
                });
              }}
              InputProps={{
                inputComponent: NumericFormatCustom,
                inputProps: {
                  prefix: "",
                  isAllowedFlag: true,
                  maxDigits: 7,
                  maxPlaces: 2,
                },
                startAdornment: loanDetails?.depositAmount > 0 && mode.deposit !== "percentage" && (
                  <InputAdornment position="start">$</InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <ToggleButtonGroup
                      value={mode.deposit}
                      exclusive
                      color="primary"
                      onChange={(event, value) => handle.handleModeChange("deposit", value)}
                      sx={{
                        mr: -1.65,
                        height: "40px",
                      }}
                    >
                      <ToggleButton value="percentage">%</ToggleButton>
                      <ToggleButton value="dollar">$</ToggleButton>
                    </ToggleButtonGroup>
                  </InputAdornment>
                ),
              }}
              type="text"
              size="small"
              fullWidth
            />
          </Box>
        </Stack>
      </Grid>

      <Grid item sm={12} md={6} lg={4}>
        <Stack direction="column">
          <Typography variant="subtitle2" sx={{ marginBottom: "5px" }}>
            Balloon {!isNaN(loanDetails?.balloon) ? Math.round(loanDetails?.balloon * 100) / 100 : 0}% |{" "}
            {(loanDetails?.balloonAmount ? loanDetails?.balloonAmount : 0).toLocaleString(
              "en-US",
              dollarStringOptions,
            )}
          </Typography>
          <Box display="flex" alignItems="center">
            <TextField
              name="balloon"
              style={{ background: "#fff" }}
              value={isBalloonPerCheck ? loanDetails?.balloon : loanDetails?.balloonAmount}
              onChange={(event) => {
                const value = event.target.value;
                const isValueUpdated = Number(value) !== Number(loanDetails?.balloon);

                if (isValueUpdated) {
                  handle.onChangeLoanDetailsField("balloon", value || 0);
                }
              }}
              onBlur={() => {
                handle.loanDetailsFn({
                  balloon: convertValueToDecimal({ value: loanDetails?.balloon }) || 0,
                  balloonAmount: convertValueToDecimal({ value: loanDetails?.balloonAmount }) || 0,
                });
              }}
              InputProps={{
                inputComponent: NumericFormatCustom,
                inputProps: {
                  prefix: "",
                  isAllowedFlag: true,
                  maxDigits: 7,
                  maxPlaces: 2,
                },
                startAdornment: loanDetails?.balloonAmount > 0 && mode.balloon !== "percentage" && (
                  <InputAdornment position="start">$</InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <ToggleButtonGroup
                      value={mode.balloon}
                      exclusive
                      color="primary"
                      onChange={(event, value) => handle.handleModeChange("balloon", value)}
                      sx={{
                        mr: -1.65,
                        height: "40px",
                      }}
                    >
                      <ToggleButton value="percentage">%</ToggleButton>
                      <ToggleButton value="dollar">$</ToggleButton>
                    </ToggleButtonGroup>
                  </InputAdornment>
                ),
              }}
              type="text"
              size="small"
              fullWidth
            />
          </Box>
        </Stack>
      </Grid>

      <Grid item sm={12} md={6} lg={4} style={{ padding: "16px 18px" }}>
        <Typography variant="subtitle2">
          Repayment term (in months)
        </Typography>
        <Box style={{ padding: "0 7px" }}>
          <Slider
            color="secondary"
            marks={[
              { value: 12, label: 12 },
              { value: 24, label: 24 },
              { value: 36, label: 36 },
              { value: 48, label: 48 },
              { value: 60, label: 60 },
            ]}
            step={12}
            min={12}
            max={60}
            value={term}
            onChange={(event) => {
              handle.onChangeLoanDetailsField("term", event.target.value)
            }}
            onChangeCommitted={(event, value) => handle.handleTerm(value)}
            defaultValue={60}
            aria-label="Default"
            valueLabelDisplay="auto"
          />
        </Box>
      </Grid>
    </Grid>
  )
}

export default LoanDetails